/**
 * AdminShortcut — small in-context button shown only to officers /
 * admins so they can jump from a viewing page (posts, competitions,
 * race scorecards, etc.) straight into the matching admin action
 * without going through the sidebar admin tree.
 *
 * Renders nothing for unauthenticated users, members, or any user
 * who isn't an officer / admin. Safe to drop on any client page —
 * the visibility check happens client-side after `useUserData`
 * resolves so the button doesn't flash for non-admins during
 * hydration.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import Link from 'next/link';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { useUserData } from '@/hooks/use-user-data';

interface Props {
  /** Where the button navigates. */
  href: string;
  /** Visible label. Keep it short — buttons sit in headers. */
  label: string;
  /** Optional leading icon. Pass a rendered JSX element. */
  icon?: ReactNode;
  /** Button visual variant. Defaults to "outline". */
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  /** Button size. Defaults to "sm". */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  /**
   * When true, renders as a borderless icon-only chip:
   *
   *   - `variant` defaults to `ghost` (no border, transparent bg)
   *   - `size` is forced to `icon` (square)
   *   - `label` becomes the `aria-label` + tooltip but is hidden
   *     visually
   *
   * Use this on dense rows (post cards, list items) where a labeled
   * button would compete with the surrounding content.
   */
  compact?: boolean;
  /** Optional tailwind classes appended to the button. */
  className?: string;
}

/**
 * Renders an admin-only shortcut button. The component returns null
 * for any caller that lacks officer / admin role, so it can be
 * placed unconditionally on public pages.
 */
export function AdminShortcut({
  href,
  label,
  icon,
  variant,
  size,
  compact = false,
  className,
}: Props) {
  const { user, loading, isOfficer, isAdmin } = useUserData();
  if (loading || !user) return null;
  if (!isOfficer && !isAdmin) return null;

  const resolvedVariant = variant ?? (compact ? 'ghost' : 'outline');
  const resolvedSize = size ?? (compact ? 'icon' : 'sm');

  return (
    <Button asChild variant={resolvedVariant} size={resolvedSize} className={className}>
      <Link
        href={href}
        aria-label={compact ? label : undefined}
        title={compact ? label : undefined}
        className={compact ? 'inline-flex items-center justify-center' : 'inline-flex items-center gap-1.5'}
      >
        {icon}
        <span className={compact ? 'sr-only' : undefined}>{label}</span>
      </Link>
    </Button>
  );
}
